/**
 * Admin Reports Service
 * Generates regulator-style report packages (workbook + data trace + notes, zipped).
 * SystemAdmin only.
 */

import { API_ENDPOINTS } from '../config/api';
import { makeAuthenticatedRequest, createApiError } from './apiService';

export interface NmvtisQuarterlyReportRequest {
  year: number;
  quarter: number;
  customStartUtc?: string | null;
  customEndUtc?: string | null;
  refundsCount?: number;
  refundsAverageAmount?: number;
  customNarrative?: string | null;
}

/**
 * Generate the AAMVA NMVTIS Consumer Access quarterly submission package.
 * Returns the zip blob and a default filename.
 */
export const generateNmvtisQuarterlyReport = async (
  request: NmvtisQuarterlyReportRequest
): Promise<{ blob: Blob; filename: string }> => {
  const response = await makeAuthenticatedRequest(
    API_ENDPOINTS.ADMIN_REPORTS.GENERATE_NMVTIS_QUARTERLY,
    {
      method: 'POST',
      body: JSON.stringify(request),
    }
  );

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  const blob = await response.blob();

  // Honor server-provided Content-Disposition filename when present.
  const cd = response.headers.get('Content-Disposition') || '';
  const match = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  const filename = match
    ? decodeURIComponent(match[1])
    : `AllStateTitle_NMVTIS_Quarterly_Report_${request.year}_Q${request.quarter}.zip`;

  return { blob, filename };
};
